import { useAuth } from "@clerk/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BuilderSelector } from "@/features/blueprints/BuilderSelector";
import { GeneratedBlueprintView } from "@/features/blueprints/GeneratedBlueprintView";
import { GuidedLockedState } from "@/features/blueprints/guided/GuidedLockedState";
import { GuidedModeFlow } from "@/features/blueprints/guided/GuidedModeFlow";
import { ApiError } from "@/features/api/apiClient";
import {
  confirmBlueprintDraft,
  generateBlueprint,
  getBlueprint,
  updateGuidedBlueprintDraft,
  updateBlueprintDraft,
} from "@/features/blueprints/blueprintApi";
import {
  BLUEPRINT_INPUT_MAX_LENGTH,
  TARGET_BUILDERS,
  type Blueprint,
  type GuidedAnswers,
  type GuidedFollowUpAnswers,
  type GuidedUnsureFlags,
  type TargetBuilder,
} from "@/features/blueprints/blueprintTypes";
import {
  cleanAssumptions,
  generateProposedAssumptions,
} from "@/features/blueprints/proposedAssumptions";
import { useAccessSummary } from "@/features/access/useAccessSummary";

type DraftStep = "input" | "guided" | "assumptions" | "placeholder";
type SaveState = "idle" | "saving" | "saved" | "error";

const generationMessages = [
  "Reading your app idea...",
  "Mapping the user journey...",
  "Defining the MVP scope...",
  "Writing the Build Prompt...",
  "Saving your blueprint...",
];

function countWords(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

function formatTargetBuilder(value: TargetBuilder): string {
  return (
    TARGET_BUILDERS.find((builder) => builder.value === value)?.label ?? value
  );
}

function isGeneratedBlueprint(value: Blueprint | null | undefined): value is Blueprint {
  return value?.status === "generated" && Boolean(value.ai_output);
}

export function BlueprintDraft() {
  const { id } = useParams<{ id: string }>();
  const { getToken, isLoaded } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { access, isLoading: isAccessLoading } = useAccessSummary();
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null);
  const [title, setTitle] = useState("");
  const [inputText, setInputText] = useState("");
  const [targetBuilder, setTargetBuilder] =
    useState<TargetBuilder>("universal");
  const [step, setStep] = useState<DraftStep>("input");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessageIndex, setGenerationMessageIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeCta, setShowUpgradeCta] = useState(false);

  // Phase 3F: confirmed assumptions state
  const [assumptionLines, setAssumptionLines] = useState<string[]>([]);

  const hasLoadedDraft = useRef(false);
  const lastSavedDraft = useRef("");

  const wordCount = useMemo(() => countWords(inputText), [inputText]);
  const isOverLimit = inputText.length > BLUEPRINT_INPUT_MAX_LENGTH;
  const canReview = inputText.trim().length > 0 && !isOverLimit;

  useEffect(() => {
    async function loadBlueprint() {
      if (!isLoaded || !id) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = await getToken();
        const response = await getBlueprint(token, id);
        setBlueprint(response.blueprint);
        setTitle(response.blueprint.title);
        setInputText(response.blueprint.input_text);
        setTargetBuilder(response.blueprint.target_builder);

        const fromStudio = searchParams.get("fromStudio") === "1";
        const startGuided = searchParams.get("startGuided") === "1";

        if (response.blueprint.status === "confirmed") {
          setStep("placeholder");
        } else if (
          response.blueprint.generation_mode === "guided" &&
          response.blueprint.guided_answers
        ) {
          setStep("guided");
        } else if (startGuided) {
          setStep("guided");
        } else if (fromStudio) {
          // Jump directly to assumptions review after fresh draft creation
          const proposed = generateProposedAssumptions({
            appIdea: response.blueprint.input_text,
            title: response.blueprint.title || undefined,
            targetBuilder: response.blueprint.target_builder as TargetBuilder,
          });
          setAssumptionLines(proposed);
          setStep("assumptions");
        } else {
          setStep("input");
        }

        lastSavedDraft.current = JSON.stringify({
          title: response.blueprint.title,
          inputText: response.blueprint.input_text,
          targetBuilder: response.blueprint.target_builder,
        });
        hasLoadedDraft.current = true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load draft");
      } finally {
        setIsLoading(false);
      }
    }

    void loadBlueprint();
  }, [getToken, id, isLoaded, searchParams]);

  useEffect(() => {
    if (!blueprint || !hasLoadedDraft.current || step !== "input") {
      return;
    }

    const nextDraft = JSON.stringify({ title, inputText, targetBuilder });

    if (nextDraft === lastSavedDraft.current) {
      return;
    }

    const timeout = window.setTimeout(async () => {
      setSaveState("saving");
      setError(null);

      try {
        const token = await getToken();
        const response = await updateBlueprintDraft(token, blueprint.id, {
          title,
          inputText,
          targetBuilder,
        });
        setBlueprint(response.blueprint);
        lastSavedDraft.current = nextDraft;
        setSaveState("saved");
      } catch (err) {
        setSaveState("error");
        setError(err instanceof Error ? err.message : "Unable to autosave draft");
      }
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [blueprint?.id, title, inputText, targetBuilder, step]);

  // When user moves from input → assumptions, generate proposed assumptions
  function handleGoToAssumptions() {
    if (!blueprint || !canReview) {
      return;
    }
    const proposed = generateProposedAssumptions({
      appIdea: inputText,
      title: title || undefined,
      targetBuilder,
    });
    setAssumptionLines(proposed);
    setStep("assumptions");
  }

  // Editable assumption list helpers
  function updateAssumptionLine(index: number, value: string) {
    setAssumptionLines((lines) =>
      lines.map((line, i) => (i === index ? value : line)),
    );
  }

  function removeAssumptionLine(index: number) {
    setAssumptionLines((lines) => lines.filter((_, i) => i !== index));
  }

  function addAssumptionLine() {
    setAssumptionLines((lines) => [...lines, ""]);
  }

  async function handleConfirmAssumptionsAndGenerate() {
    if (!blueprint || isGenerating) {
      return;
    }

    const cleaned = cleanAssumptions(assumptionLines);

    setIsGenerating(true);
    setIsConfirming(true);
    setGenerationMessageIndex(0);
    setError(null);
    setShowUpgradeCta(false);

    const interval = window.setInterval(() => {
      setGenerationMessageIndex(
        (currentIndex) => (currentIndex + 1) % generationMessages.length,
      );
    }, 3000);

    let token: string | null = null;

    try {
      token = await getToken();
      // Confirm the draft first (sets status to "confirmed")
      const confirmed = await confirmBlueprintDraft(token, blueprint.id);
      setBlueprint(confirmed.blueprint);
      // Get a fresh token for the generate call — the confirm round-trip may have
      // taken enough time for a short-lived dev JWT to expire.
      const generateToken = await getToken();
      const generated = await generateBlueprint(
        generateToken,
        blueprint.id,
        cleaned.length > 0 ? cleaned : undefined,
      );
      setBlueprint(generated.blueprint);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(err.message);
        setShowUpgradeCta(true);
      } else {
        const pollingDeadline = Date.now() + 5 * 60 * 1000;
        let recovered = false;
        while (Date.now() < pollingDeadline) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 5_000));
          recovered = await refetchGeneratedBlueprint(blueprint.id);
          if (recovered) break;
        }
        if (recovered) {
          return;
        }
        setError(
          "We could not generate your blueprint this time. Your app idea is still saved. Please try again in a moment.",
        );
      }
    } finally {
      window.clearInterval(interval);
      setIsGenerating(false);
      setIsConfirming(false);
    }
  }

  async function handleGenerate() {
    if (!blueprint || isGenerating) {
      return;
    }

    setIsGenerating(true);
    setGenerationMessageIndex(0);
    setError(null);
    setShowUpgradeCta(false);

    const interval = window.setInterval(() => {
      setGenerationMessageIndex(
        (currentIndex) => (currentIndex + 1) % generationMessages.length,
      );
    }, 3000);

    let token: string | null = null;

    try {
      token = await getToken();
      const response = await generateBlueprint(token, blueprint.id);
      setBlueprint(response.blueprint);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(err.message);
        setShowUpgradeCta(true);
      } else {
        // Network/connection error — the proxy may have dropped the long-running
        // request while the LLM was still running server-side. Keep isGenerating=true
        // and poll until the server writes the result, rather than failing immediately.
        const pollingDeadline = Date.now() + 5 * 60 * 1000;
        let recovered = false;
        while (Date.now() < pollingDeadline) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 5_000));
          recovered = await refetchGeneratedBlueprint(blueprint.id);
          if (recovered) break;
        }
        if (recovered) {
          return;
        }
        setError(
          "We could not generate your blueprint this time. Your app idea is still saved. Please try again in a moment.",
        );
      }
    } finally {
      window.clearInterval(interval);
      setIsGenerating(false);
    }
  }

  async function handleGuidedSave(
    guidedAnswers: GuidedAnswers,
    smartFollowupAnswers: GuidedFollowUpAnswers,
    unsureFlags: GuidedUnsureFlags,
  ) {
    if (!blueprint) {
      return;
    }

    setSaveState("saving");
    setError(null);
    setShowUpgradeCta(false);

    try {
      const token = await getToken();
      const response = await updateGuidedBlueprintDraft(token, blueprint.id, {
        guidedAnswers,
        smartFollowupAnswers,
        unsureFlags,
        title: guidedAnswers.q1?.projectName,
        targetBuilder,
      });
      setBlueprint(response.blueprint);
      setTitle(response.blueprint.title);
      setInputText(response.blueprint.input_text);
      setTargetBuilder(response.blueprint.target_builder);
      setSaveState("saved");
    } catch (err) {
      setSaveState("error");
      setError(
        err instanceof Error ? err.message : "Unable to autosave guided brief",
      );
      throw err;
    }
  }

  async function handleGuidedGenerate() {
    if (!blueprint || isGenerating) {
      return;
    }

    setIsGenerating(true);
    setGenerationMessageIndex(0);
    setError(null);
    setShowUpgradeCta(false);

    const interval = window.setInterval(() => {
      setGenerationMessageIndex(
        (currentIndex) => (currentIndex + 1) % generationMessages.length,
      );
    }, 3000);

    let token: string | null = null;

    try {
      token = await getToken();
      const confirmed = await confirmBlueprintDraft(token, blueprint.id);
      setBlueprint(confirmed.blueprint);
      // Get a fresh token for generate — the confirm round-trip may have taken
      // enough time for a short-lived dev JWT to expire.
      const generateToken = await getToken();
      const generated = await generateBlueprint(generateToken, blueprint.id);
      setBlueprint(generated.blueprint);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(err.message);
        setShowUpgradeCta(true);
      } else {
        const pollingDeadline = Date.now() + 5 * 60 * 1000;
        let recovered = false;
        while (Date.now() < pollingDeadline) {
          await new Promise<void>((resolve) => window.setTimeout(resolve, 5_000));
          recovered = await refetchGeneratedBlueprint(blueprint.id);
          if (recovered) break;
        }
        if (recovered) {
          return;
        }
        setError(
          "We could not generate your blueprint this time. Your guided brief is still saved. Please try again in a moment.",
        );
      }
    } finally {
      window.clearInterval(interval);
      setIsGenerating(false);
    }
  }

  async function refetchGeneratedBlueprint(blueprintId: string): Promise<boolean> {
    try {
      const token = await getToken();
      const response = await getBlueprint(token, blueprintId);

      if (!isGeneratedBlueprint(response.blueprint)) {
        return false;
      }

      setBlueprint(response.blueprint);
      setError(null);
      setShowUpgradeCta(false);
      return true;
    } catch {
      return false;
    }
  }

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-lg bg-white" />;
  }

  if (error && !blueprint) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-800">
        {error}
      </div>
    );
  }

  if (!blueprint) {
    return null;
  }

  if (blueprint.status === "generated" && blueprint.ai_output) {
    // ?view=idea shows the original source app idea read-only for QA and context review
    if (searchParams.get("view") === "idea") {
      return (
        <div className="space-y-6">
          <Link
            to={`/blueprints/${blueprint.id}`}
            className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
          >
            ← Back to blueprint
          </Link>
          <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm font-medium text-indigo-700">Source app idea</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
              {blueprint.title}
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              This is the original app idea used to generate the blueprint. It is
              read-only here.
            </p>
            <div className="mt-5 grid gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Target builder
                </p>
                <div className="mt-2 rounded-lg bg-slate-100 p-3 text-sm text-slate-700">
                  {formatTargetBuilder(blueprint.target_builder)}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  App idea
                  {blueprint.generation_mode === "guided" ? (
                    <span className="ml-2 font-normal text-slate-500">
                      (compiled from Guided mode answers)
                    </span>
                  ) : null}
                </p>
                <div className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-100 p-4 text-sm leading-6 text-slate-700">
                  {blueprint.input_text || "No app idea text recorded."}
                </div>
              </div>
            </div>
          </section>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <Link
          to={`/projects/${blueprint.project_id}`}
          className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          Back to project
        </Link>
        <GeneratedBlueprintView
          blueprintId={blueprint.id}
          title={blueprint.title}
          output={blueprint.ai_output}
          targetBuilder={blueprint.target_builder}
          generationVersion={blueprint.generation_version}
          status={blueprint.status}
          generatedAt={blueprint.updated_at}
          rating={blueprint.rating}
          ratingFeedback={blueprint.rating_feedback}
          outputAccess={blueprint.output_access}
          onBlueprintUpdated={setBlueprint}
          projectName={blueprint.project_name ?? null}
          inputText={blueprint.input_text}
          onRegenerate={handleGenerate}
          isRegenerating={isGenerating}
        />
      </div>
    );
  }

  // step === "placeholder" — blueprint already confirmed (e.g. from a previous session)
  if (step === "placeholder") {
    return (
      <div className="space-y-6">
        <Link
          to={`/projects/${blueprint.project_id}`}
          className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          Back to project
        </Link>
        <section className="rounded-lg border border-slate-200 bg-white p-6">
          <p className="text-sm font-medium text-indigo-700">Ready to generate</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
            Your app idea is confirmed
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            Review the details below, then generate your build-ready blueprint.
            This usually takes about 20–30 seconds.
          </p>
          <div className="mt-5 grid gap-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">
                Draft title
              </p>
              <div className="mt-2 rounded-lg bg-slate-100 p-4 text-sm leading-6 text-slate-700">
                {blueprint.title}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">
                Target builder
              </p>
              <div className="mt-2 rounded-lg bg-slate-100 p-4 text-sm leading-6 text-slate-700">
                {formatTargetBuilder(blueprint.target_builder)}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">App idea</p>
              <div className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-100 p-4 text-sm leading-6 text-slate-700">
                {blueprint.input_text}
              </div>
            </div>
          </div>
          {error ? (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <p>{error}</p>
              {showUpgradeCta ? (
                <Link
                  to="/pricing"
                  className="mt-3 inline-flex rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800"
                >
                  View pricing
                </Link>
              ) : null}
            </div>
          ) : null}
          {isGenerating ? (
            <div className="mt-5 rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
              <p className="font-semibold">
                {generationMessages[generationMessageIndex]}
              </p>
              <p className="mt-1">This usually takes about 20-30 seconds.</p>
            </div>
          ) : null}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setStep("input")}
              disabled={isGenerating}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Edit app idea
            </button>
            <button
              type="button"
              onClick={() => void handleGenerate()}
              disabled={isGenerating}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {isGenerating ? "Generating..." : "Generate blueprint"}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // step === "guided"
  if (step === "guided") {
    return (
      <div className="space-y-6">
        <Link
          to={`/projects/${blueprint.project_id}`}
          className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          Back to project
        </Link>
        {showUpgradeCta && error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <p>{error}</p>
            <Link
              to="/pricing"
              className="mt-3 inline-flex rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800"
            >
              View pricing
            </Link>
          </div>
        ) : null}
        <GuidedModeFlow
          blueprint={blueprint}
          saveState={saveState}
          isGenerating={isGenerating}
          generationMessage={generationMessages[generationMessageIndex]}
          error={showUpgradeCta ? null : error}
          onSave={handleGuidedSave}
          onBackToFastMode={() => setStep("input")}
          onGenerate={() => void handleGuidedGenerate()}
          onSaveAndFinishLater={() => navigate(`/projects/${blueprint.project_id}`)}
        />
      </div>
    );
  }

  // step === "assumptions" — Phase 3F: Confirm Build Assumptions screen
  if (step === "assumptions") {
    const hasAssumptions = assumptionLines.some((l) => l.trim().length > 0);

    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setStep("input")}
          className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
        >
          ← Edit app idea
        </button>

        {/* Step indicator */}
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="rounded-full bg-slate-200 px-2.5 py-0.5 font-medium text-slate-500">
            Step 1 — Done
          </span>
          <span className="text-slate-300">→</span>
          <span className="rounded-full bg-indigo-600 px-2.5 py-0.5 font-semibold text-white">
            Step 2 — Confirm assumptions
          </span>
          <span className="text-slate-300">→</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-400">
            Step 3 — Generate
          </span>
        </div>

        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <span className="inline-block rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-700">
            Build Assumptions
          </span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-950">
            Confirm build assumptions
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            Review these assumptions before generation. They help Alchemy create
            a stronger, more specific VIBERC build prompt. Edit or remove any
            assumption that doesn't apply.
          </p>

          {/* App idea preview */}
          <div className="mt-5 rounded-lg border border-slate-100 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Your app idea
            </p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {inputText}
            </p>
          </div>

          {/* Assumption lines */}
          <div className="mt-6">
            <p className="mb-3 text-sm font-semibold text-slate-800">
              Proposed assumptions
              <span className="ml-2 font-normal text-slate-500">
                — edit or remove any that don't apply
              </span>
            </p>

            <div className="space-y-2">
              {assumptionLines.map((line, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="mt-2 flex-shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold text-indigo-700">
                    {index + 1}
                  </div>
                  <input
                    type="text"
                    value={line}
                    onChange={(e) => updateAssumptionLine(index, e.target.value)}
                    maxLength={500}
                    className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                  />
                  <button
                    type="button"
                    onClick={() => removeAssumptionLine(index)}
                    aria-label="Remove assumption"
                    className="mt-1.5 flex-shrink-0 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-400 transition hover:border-red-300 hover:text-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addAssumptionLine}
              className="mt-3 rounded-md border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-slate-500 transition hover:border-indigo-400 hover:text-indigo-700"
            >
              + Add assumption
            </button>
          </div>

          {/* Info note about recommended V1 additions */}
          <div className="mt-5 rounded-lg border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-semibold">About recommended V1 additions</p>
            <p className="mt-1 leading-6">
              Items listed as "Recommended V1 additions to confirm" are proposed
              enhancements. Leave them in to include them in the generated
              blueprint, or remove them to keep the scope simpler.
            </p>
          </div>

          {/* Error */}
          {error ? (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <p>{error}</p>
              {showUpgradeCta ? (
                <Link
                  to="/pricing"
                  className="mt-3 inline-flex rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800"
                >
                  View pricing
                </Link>
              ) : null}
            </div>
          ) : null}

          {/* Generation progress */}
          {isGenerating ? (
            <div className="mt-5 rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
              <p className="font-semibold">
                {generationMessages[generationMessageIndex]}
              </p>
              <p className="mt-1">
                {isConfirming
                  ? "Confirming your build assumptions..."
                  : "This usually takes about 20–30 seconds."}
              </p>
            </div>
          ) : null}

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleConfirmAssumptionsAndGenerate()}
              disabled={isGenerating || !hasAssumptions}
              className="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {isGenerating
                ? "Generating blueprint..."
                : "Confirm assumptions and generate blueprint"}
            </button>
            <button
              type="button"
              onClick={() => setStep("input")}
              disabled={isGenerating}
              className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Edit app idea
            </button>
          </div>
        </section>
      </div>
    );
  }

  // step === "input" — the default edit/entry form
  return (
    <div className="space-y-6">
      <Link
        to={`/projects/${blueprint.project_id}`}
        className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to project
      </Link>
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium text-indigo-700">Step 1</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
              Describe your app idea
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              Describe the app you want to build. Alchemy will propose build
              assumptions for you to review and confirm before generating the
              VIBERC-ready blueprint.
            </p>
          </div>
          <span className="rounded-md bg-slate-100 px-3 py-1 text-sm font-semibold capitalize text-slate-700">
            {saveState === "idle" ? "Loaded" : saveState}
          </span>
        </div>

        {error ? (
          <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <p>{error}</p>
            {showUpgradeCta ? (
              <Link
                to="/pricing"
                className="mt-3 inline-flex rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800"
              >
                View pricing
              </Link>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6">
          {isAccessLoading ? (
            <div className="h-28 animate-pulse rounded-lg bg-slate-100" />
          ) : access?.capabilities.canUseGuidedMode ? (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-5">
              <p className="text-sm font-semibold text-indigo-950">
                Guided Mode
              </p>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-indigo-900">
                Answer five focused questions and review a clearer brief before
                generating your blueprint.
              </p>
              <button
                type="button"
                onClick={() => setStep("guided")}
                className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                Guide me through this
              </button>
            </div>
          ) : (
            <GuidedLockedState />
          )}
        </div>

        <div className="mt-6 grid gap-5">
          <div>
            <label
              htmlFor="blueprint-title"
              className="block text-sm font-medium text-slate-700"
            >
              Draft title
            </label>
            <input
              id="blueprint-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={160}
              className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <BuilderSelector
            value={targetBuilder}
            onChange={setTargetBuilder}
          />

          <div>
            <label
              htmlFor="fast-mode-input"
              className="block text-sm font-medium text-slate-700"
            >
              App idea
            </label>
            <textarea
              id="fast-mode-input"
              value={inputText}
              onChange={(event) => setInputText(event.target.value)}
              maxLength={BLUEPRINT_INPUT_MAX_LENGTH}
              rows={10}
              placeholder="Describe your app idea: what it does, who it is for, and what problem it solves."
              className="mt-2 w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <div className="mt-2 flex flex-col gap-2 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {wordCount > 0 && wordCount < 30
                  ? "A little more detail will make your blueprint stronger - aim for 2-3 sentences."
                  : "Your app idea can be up to 5,000 characters."}
              </span>
              <span>
                {inputText.length} / {BLUEPRINT_INPUT_MAX_LENGTH}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleGoToAssumptions}
            disabled={!canReview}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
          >
            Review build assumptions →
          </button>
        </div>
      </section>
    </div>
  );
}
